<template>
  <div class="w-full">
    <!-- Location Search -->
    <UInputMenu
      v-model="selectedItem"
      v-model:search-term="searchTerm"
      :items="items"
      :loading="loading"
      ignore-filter
      :label-key="'label'"
      icon="i-heroicons-magnifying-glass-20-solid"
      :placeholder="t('views.cottage-list.search.placeholder')"
      :aria-label="t('views.cottage-list.search.placeholder')"
      size="xl"
      :content="{
        avoidCollisions: false,
      }"
    >
      <template #trailing>
        <span class="sr-only">{{
          t('views.cottage-list.search.searchOptions')
        }}</span>
      </template>
      <template #item="{ item }">
        <div v-if="item" class="flex">
          <div>
            <UIcon
              :name="
                item.rawType === 'COTTAGE'
                  ? 'i-heroicons-home'
                  : 'i-heroicons-map-pin'
              "
              class="h-5 w-5 text-escapadaGray-400"
              aria-hidden="true"
            />
          </div>
          <div class="flex flex-col ml-2">
            <TText size="sm" classes="mb-1">
              {{ item.label }}
            </TText>
            <TText size="xs" color="text-gray-600" classes="mb-1">
              ({{ item.description }})
            </TText>
          </div>
        </div>
      </template>
      <template #empty>
        {{ t('views.cottage-list.search.noResults') }}
      </template>
    </UInputMenu>
  </div>
</template>

<script setup lang="ts">
import { browserFetch as fetchSearchSuggest } from '~~/modules/api/rurality/v3/search/suggest'

interface Props {
  modelValue?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
})

const emit = defineEmits<{
  locationSelected: [location: string]
}>()

const loading = ref(false)
const searchTerm = ref<string>('')

type CottageSuggestion = {
  type: 'item'
  rawType: 'COTTAGE'
  name: string
  denomination?: string
  parentName?: string
  description?: string
  url: string
  location: null
  label: string
}

type LocationSuggestion = {
  type: 'item'
  rawType: 'village' | 'province' | 'region' | 'country' | 'zone'
  name: string
  denomination?: string
  parentName?: string
  description?: string
  url: null
  location: string
  label: string
}

type Suggestion = CottageSuggestion | LocationSuggestion

function createPlaceholderSuggestion(
  locationName: string,
): LocationSuggestion | undefined {
  if (!locationName) return undefined

  return {
    type: 'item',
    rawType: 'zone',
    name: locationName,
    label: locationName,
    url: null,
    location: '',
  }
}

const items = ref<Suggestion[]>([])
const selectedItem = ref<Suggestion | undefined>(
  createPlaceholderSuggestion(props.modelValue),
)

watch(
  () => props.modelValue,
  newValue => {
    selectedItem.value = createPlaceholderSuggestion(newValue)
  },
)

watch(
  () => searchTerm.value,
  async searchTerm => {
    if (searchTerm && searchTerm.length > 2) {
      items.value = await search(searchTerm)
    } else {
      items.value = []
    }
  },
)

watch(
  () => selectedItem.value,
  selection => {
    if (!selection) return

    if (selection.rawType === 'COTTAGE') {
      if (import.meta.client) {
        navigateTo(
          routes().filteredCottage({
            url: selection.url,
            dateIn: undefined,
            dateOut: undefined,
            capacity: undefined,
          }),
          {
            external: true,
          },
        )
      }
      return
    }

    emit('locationSelected', selection.location)
  },
)

function mapSuggestionToSuggestion(suggestion: any): Suggestion {
  const transformNullToUndefined = (value: string | null | undefined) =>
    value === null ? undefined : value

  if (suggestion.type === 'COTTAGE') {
    const result: CottageSuggestion = {
      ...suggestion,
      type: 'item',
      rawType: 'COTTAGE',
      label: suggestion.name,
      location: null,
      denomination: transformNullToUndefined(suggestion.denomination),
      parentName: transformNullToUndefined(suggestion.parentName),
      description: transformNullToUndefined(suggestion.description),
    }
    return result
  } else {
    const result: LocationSuggestion = {
      ...suggestion,
      type: 'item',
      rawType: suggestion.type,
      label: suggestion.name,
      url: null,
      denomination: transformNullToUndefined(suggestion.denomination),
      parentName: transformNullToUndefined(suggestion.parentName),
      description: transformNullToUndefined(suggestion.description),
    }
    return result
  }
}

async function search(q: string) {
  loading.value = true

  const data = await fetchSearchSuggest({
    query: q,
  })

  loading.value = false

  if (data.success) {
    return data.success.data.suggestions.map(mapSuggestionToSuggestion)
  }

  return []
}
</script>
